import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Phone,
  Search,
  CheckCircle,
  Delete
} from '@mui/icons-material';
import TwilioService, { AvailablePhoneNumber, TwilioConfig } from '../services/TwilioService';
import { useAuth } from '../contexts/AuthContext';

interface TwilioPhoneNumberSettingsProps {
  onConfigured?: () => void;
  showAsModal?: boolean;
  onClose?: () => void;
  required?: boolean; // If true, user cannot close modal until configured
}

const TwilioPhoneNumberSettings: React.FC<TwilioPhoneNumberSettingsProps> = ({
  onConfigured,
  showAsModal = false,
  onClose,
  required = false
}) => {
  const { currentUser } = useAuth();
  const [config, setConfig] = useState<TwilioConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [areaCode, setAreaCode] = useState('');
  const [availableNumbers, setAvailableNumbers] = useState<AvailablePhoneNumber[]>([]);
  const [showSearchDialog, setShowSearchDialog] = useState(false);
  const [showReleaseDialog, setShowReleaseDialog] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const twilioService = useRef(new TwilioService());
  const areaCodeInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (currentUser?.companyId) {
      fetchConfig();
    }
  }, [currentUser?.companyId]);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await twilioService.current.getCompanyTwilioConfig();
      setConfig(data);
    } catch (err: any) {
      console.error('Error fetching Twilio config:', err);
      // Don't show error for TWILIO_NOT_CONFIGURED - it's expected for new companies
      if (err.code !== 'TWILIO_NOT_CONFIGURED') {
        setError(err.message || 'Failed to load Twilio configuration');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!areaCode || areaCode.length !== 3) {
      setError('Please enter a valid 3-digit area code');
      return;
    }

    try {
      setSearching(true);
      setError(null);
      setSuccess(null);
      const numbers = await twilioService.current.searchAvailableNumbers(areaCode);
      setAvailableNumbers(numbers);

      if (numbers.length === 0) {
        setError('No available numbers found for this area code');
      }
    } catch (err: any) {
      console.error('Error searching numbers:', err);
      setError(err.message || 'Failed to search for phone numbers');
      setAvailableNumbers([]);
    } finally {
      setSearching(false);
    }
  };

  const handlePurchase = async (phoneNumber: string) => {
    try {
      setPurchasing(true);
      setError(null);
      setSuccess(null);

      const result = await twilioService.current.purchaseNumber(phoneNumber);
      setSuccess(result.message);
      setShowSearchDialog(false);
      setAvailableNumbers([]);
      setAreaCode('');

      // Refresh config
      await fetchConfig();

      // Notify parent component
      if (onConfigured) {
        onConfigured();
      }
    } catch (err: any) {
      console.error('Error purchasing number:', err);
      setError(err.message || 'Failed to purchase phone number');
    } finally {
      setPurchasing(false);
    }
  };

  const handleRelease = async () => {
    try {
      setReleasing(true);
      setError(null);
      setSuccess(null);

      const result = await twilioService.current.releaseNumber();
      setShowReleaseDialog(false);

      // Refresh config
      await fetchConfig();

      // Show success dialog
      setSuccessMessage(result.message || 'Phone number released successfully');
      setShowSuccessDialog(true);
    } catch (err: any) {
      console.error('Error releasing number:', err);
      setError(err.message || 'Failed to release phone number');
      setShowReleaseDialog(false);
    } finally {
      setReleasing(false);
    }
  };

  const formatPhoneNumber = (phoneNumber: string) => {
    // Format +17025551234 to (702) 555-1234
    const cleaned = phoneNumber.replace(/\D/g, '');
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      const areaCode = cleaned.slice(1, 4);
      const prefix = cleaned.slice(4, 7);
      const line = cleaned.slice(7);
      return `(${areaCode}) ${prefix}-${line}`;
    }
    return phoneNumber;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  const content = (
    <>
      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <Phone color="primary" fontSize="large" />
        <Typography variant="h5">SMS Phone Number Settings</Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" mb={3}>
        Configure your company's phone number for sending SMS messages to customers
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {config ? (
        <Box>
          <Paper variant="outlined" sx={{ p: 3, mb: 2, bgcolor: 'success.50' }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <CheckCircle color="success" />
                  <Typography variant="h6">Active Phone Number</Typography>
                </Box>
                <Typography variant="h4" color="primary" gutterBottom>
                  {formatPhoneNumber(config.phoneNumber)}
                </Typography>
                <Box display="flex" gap={1} mt={1}>
                  <Chip
                    label={config.status.toUpperCase()}
                    color="success"
                    size="small"
                  />
                  <Chip
                    label={`${config.locality || ''}, ${config.region || ''}`}
                    size="small"
                    variant="outlined"
                  />
                </Box>
              </Box>
              <Tooltip title="Release this number">
                <IconButton
                  onClick={() => setShowReleaseDialog(true)}
                  color="error"
                  disabled={releasing}
                >
                  <Delete />
                </IconButton>
              </Tooltip>
            </Box>
          </Paper>

          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            This phone number will be used to send all SMS messages to your customers, including appointment reminders and confirmations.
          </Typography>
        </Box>
      ) : (
        <Box>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              SMS Phone Number Setup
            </Typography>
            <Typography variant="body2" paragraph>
              A dedicated phone number is essential for your business communication:
            </Typography>
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
              <li>
                <Typography variant="body2">
                  <strong>Appointment Reminders:</strong> Automatically remind customers about upcoming appointments
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>Customer Communication:</strong> Send confirmations, updates, and follow-ups via SMS
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>Professional Presence:</strong> Use a local number that customers recognize and trust
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>Two-Way Messaging:</strong> Customers can reply directly to your messages
                </Typography>
              </li>
            </Box>
          </Alert>

          <Button
            variant="contained"
            startIcon={<Search />}
            onClick={() => setShowSearchDialog(true)}
            fullWidth
            size="large"
          >
            Search for Available Phone Numbers
          </Button>
        </Box>
      )}

      {/* Search Dialog */}
      <Dialog
        open={showSearchDialog}
        onClose={() => setShowSearchDialog(false)}
        maxWidth="sm"
        fullWidth
        disablePortal={showAsModal}
        disableEnforceFocus
        disableAutoFocus
        disableRestoreFocus
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Search />
            Search Available Phone Numbers
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Enter an area code to search for available phone numbers in that region
          </Typography>

          <Box display="flex" gap={2} mb={3}>
            <TextField
              label="Area Code"
              value={areaCode}
              onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, '').slice(0, 3))}
              placeholder="702"
              inputProps={{ maxLength: 3 }}
              fullWidth
              disabled={searching}
              autoFocus
              inputRef={areaCodeInputRef}
              onClick={() => {
                // Force focus after any dialog focus management completes
                setTimeout(() => areaCodeInputRef.current?.focus(), 0);
              }}
            />
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={searching || areaCode.length !== 3}
              startIcon={searching ? <CircularProgress size={20} /> : <Search />}
            >
              Search
            </Button>
          </Box>

          {availableNumbers.length > 0 && (
            <>
              <Typography variant="subtitle2" gutterBottom>
                Available Numbers ({availableNumbers.length})
              </Typography>
              <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                {availableNumbers.map((number) => (
                  <ListItem
                    key={number.phoneNumber}
                    disablePadding
                    secondaryAction={
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handlePurchase(number.phoneNumber)}
                        disabled={purchasing}
                      >
                        {purchasing ? <CircularProgress size={20} /> : 'Select'}
                      </Button>
                    }
                  >
                    <ListItemButton>
                      <ListItemText
                        primary={formatPhoneNumber(number.phoneNumber)}
                        secondary={`${number.locality || ''}, ${number.region || ''}`}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSearchDialog(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Release Confirmation Dialog */}
      <Dialog
        open={showReleaseDialog}
        onClose={() => setShowReleaseDialog(false)}
      >
        <DialogTitle>Release Phone Number?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              Warning: This action cannot be undone
            </Typography>
            <Typography variant="body2">
              Releasing this phone number will:
            </Typography>
            <ul style={{ marginTop: 8, marginBottom: 0 }}>
              <li>
                <Typography variant="body2">
                  Remove the number from your account
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Stop all SMS message sending capabilities
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Make the number available for others to purchase
                </Typography>
              </li>
            </ul>
          </Alert>
          <Typography variant="body2">
            Are you sure you want to release <strong>{config && formatPhoneNumber(config.phoneNumber)}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowReleaseDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleRelease}
            color="error"
            variant="contained"
            disabled={releasing}
            startIcon={releasing ? <CircularProgress size={20} /> : <Delete />}
          >
            Release Number
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Confirmation Dialog */}
      <Dialog
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <CheckCircle color="success" />
            Success
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {successMessage}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            You can now purchase a new phone number if needed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSuccessDialog(false)} variant="contained" color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  if (showAsModal) {
    return (
      <Dialog
        open={true}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        disableEnforceFocus={showSearchDialog} // Disable focus enforcement when search dialog is open
      >
        <DialogContent sx={{ p: 3 }}>
          {required && !config && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                Setup Required to Continue
              </Typography>
              <Typography variant="body2">
                Before you can use the application, you need to set up an SMS phone number. This number will be used to:
              </Typography>
              <Box component="ul" sx={{ mt: 1, mb: 1, pl: 2 }}>
                <li><Typography variant="body2">Send appointment reminders to your customers</Typography></li>
                <li><Typography variant="body2">Communicate updates and confirmations</Typography></li>
                <li><Typography variant="body2">Enable two-way messaging with clients</Typography></li>
              </Box>
              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                Don't worry - this only takes a minute! Search for a local number below.
              </Typography>
            </Alert>
          )}
          {content}
        </DialogContent>
        <DialogActions>
          {!config && onClose && (
            <Button onClick={onClose} color="inherit">
              Skip for Now
            </Button>
          )}
          {config && onClose && (
            <Button onClick={onClose}>
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      {content}
    </Paper>
  );
};

export default TwilioPhoneNumberSettings;
